import React, { useState } from 'react'
import './Input.css'

/**
 * Reusable Input Component
 * @param {Object} props - Component props
 * @param {string} props.type - Input type
 * @param {string} props.label - Input label
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.error - Error message
 * @param {boolean} props.required - Required field
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref for React Hook Form compatibility
 */
const Input = React.forwardRef(
	(
		{
			type = 'text',
			label,
			placeholder,
			value,
			onChange,
			error,
			required = false,
			disabled = false,
			className = '',
			id,
			...props
		},
		ref
	) => {
		const [isFocused, setIsFocused] = useState(false)

		const inputId = id || `input-${label?.replace(/\s+/g, '-').toLowerCase()}`
		const baseClass = 'input'
		const wrapperClasses = [
			`${baseClass}-wrapper`,
			error && `${baseClass}-wrapper--error`,
			isFocused && `${baseClass}-wrapper--focused`,
			disabled && `${baseClass}-wrapper--disabled`,
			className,
		]
			.filter(Boolean)
			.join(' ')

		return (
			<div className={wrapperClasses}>
				{label && (
					<label htmlFor={inputId} className={`${baseClass}__label`}>
						{label}
						{required && <span className={`${baseClass}__required`}>*</span>}
					</label>
				)}
				<input
					id={inputId}
					type={type}
					className={`${baseClass}__field`}
					placeholder={placeholder}
					value={value}
					onChange={onChange}
					disabled={disabled}
					required={required}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					ref={ref}
					{...props}
				/>
				{error && <span className={`${baseClass}__error`}>{error}</span>}
			</div>
		)
	}
)

Input.displayName = 'Input'

export default Input
